import clsx from 'clsx';
import { ImageFetchPriority, useImage } from './ImageProvider';
import * as styles from './Image.module.css';

type ImageFormat = 'jpg' | 'png' | 'webp' | 'gif' | 'avif';

type ImageSize = {
  width: number;
  height: number;
};

export type ImageType = {
  contentful_id: string;
  title: string;
  file: {
    url: string;
    contentType: `image/${string}`;
    details: {
      image: ImageSize;
    };
  };
};

export type ImageElementAttributes = Omit<JSX.IntrinsicElements['img'], 'width' | 'height'>;

export type ImageComponentProps = {
  image?: ImageType;
  width?: number;
  height?: number;
  format?: ImageFormat;
  quality?: number;
  fetchPriority?: ImageFetchPriority;
};

const createURL = (url: string) => new URL(url, `https:${url}`);

export const Image: React.FC<ImageElementAttributes & ImageComponentProps> = ({
  image,
  format,
  quality,
  width,
  height,
  loading,
  fetchPriority,
  className,
  ...props
}) => {
  const { loading: loadingFromImageContext, fetchPriority: fetchPriorityFromImageContext } = useImage();
  if (!!loadingFromImageContext && !loading) loading = loadingFromImageContext;
  if (!!fetchPriorityFromImageContext && !fetchPriority) fetchPriority = fetchPriorityFromImageContext;

  if (image) {
    const sharedProps: JSX.IntrinsicElements['img'] = {
      width: image.file.details.image.width,
      height: image.file.details.image.height,
      alt: props.alt ?? image.title,
      loading: loading ?? 'lazy',
      // @ts-ignore
      fetchpriority: fetchPriority,
      decoding: 'async',
    };

    if (image.file.contentType === 'image/svg+xml')
      return (
        <img {...props} {...sharedProps} className={className} src={createURL(image.file.url).toString()} role='img' />
      );

    if (!format) format = 'webp';
    if (!quality) quality = 50;

    const createSrc = (imageSize?: Partial<ImageSize>): string => {
      const url = createURL(image.file.url);
      url.searchParams.append('fm', format || 'webp');
      if (format === 'jpg') url.searchParams.append('fl', 'progressive');
      if (quality) url.searchParams.append('q', quality.toString());
      if (imageSize?.width) url.searchParams.append('w', imageSize.width.toString());
      if (imageSize?.height) url.searchParams.append('h', imageSize.height.toString());
      return url.toString();
    };

    const createSrcSet = (widths: number[]): string =>
      widths.map((width) => `${createSrc({ width })} ${width}w`).join(', ');

    return (
      <img
        {...props}
        {...sharedProps}
        className={clsx(styles.image, className)}
        sizes='100vw'
        src={width || height ? createSrc({ width, height }) : createSrc({ width: 760 })}
        srcSet={!width && !height ? createSrcSet([760, 960, 1360]) : undefined}
      />
    );
  } else if (props.src) {
    const url = createURL(props.src);
    if (format) url.searchParams.append('fm', format);
    if (quality) url.searchParams.append('q', quality.toString());
    if (width) url.searchParams.append('w', width.toString());
    if (height) url.searchParams.append('h', height.toString());

    return (
      <img
        {...props}
        src={url.toString()}
        className={clsx(styles.image, className)}
        width={width}
        height={height}
        loading={loading || 'lazy'}
        // @ts-ignore
        fetchpriority={fetchPriority}
        decoding='async'
      />
    );
  } else {
    throw new Error(`Image component must be provided either an 'image' or a 'src' prop.`);
  }
};
